/** Stato di prova e abbonamento, calcolato dal solo profilo utente.
 *
 *  Sta qui e non dentro AuthContext perche' lo leggono due strade diverse, e
 *  devono leggere la stessa cosa: il contesto ad ogni cambio di sessione, e la
 *  ripresa della sessione dalla cache, che deve sapere se l'account e' scaduto
 *  *prima* di decidere se tenere la persona dentro l'app.
 *  Due copie di questa regola divergerebbero al primo cambio di piano. */

const NESSUN_VINCOLO = {
  isTrialActive: false,
  isTrialExpired: false,
  trialDaysLeft: 0,
  canAccessApp: true,
};

export const computeTrialSubscriptionState = (user) => {
  const none = { ...NESSUN_VINCOLO };
  if (!user || typeof user !== "object") return none;

  // Admins always have access (role='admin' is authoritative; is_admin is legacy fallback).
  if (user.role === "admin" || user.is_admin) return none;

  const collaborator = user.is_collaborator || user.role === "collaborator";
  if (collaborator) {
    const hasActive = (user.active_shared_count || 0) > 0;
    const workspaceBlocked = user.workspace_access?.allowed === false;
    return { ...none, canAccessApp: hasActive && !workspaceBlocked,
      collaboratorNoAccess: !hasActive || workspaceBlocked,
      collaboratorWorkspaceBlocked: workspaceBlocked };
  }

  const status = user.subscription_status;
  const now = Date.now();
  const future = (date) => Boolean(date) && new Date(date).getTime() > now;
  const trialDaysLeft = Math.max(0, Math.ceil((new Date(user.trial_ends_at).getTime() - now) / 86400000)) || 0;
  let allowed = false;
  if (!user.deletion_pending && status !== "suspended") {
    if (user.lifetime) allowed = true;
    else if (status === "active") allowed = !user.plan_expires_at || future(user.plan_expires_at);
    else if (status === "trialing") allowed = Boolean(user.stripe_customer_id) && future(user.trial_ends_at);
    else if (status === "past_due") allowed = Boolean(user.stripe_customer_id);
    if (!user.lifetime && user.cancel_at && !future(user.cancel_at)) allowed = false;
  }
  // /auth/me is authoritative. Local calculation only covers login/cache until
  // that response arrives; the API checks every operation independently.
  const canAccessApp = typeof user.workspace_access?.allowed === "boolean"
    ? user.workspace_access.allowed : allowed;
  return { isTrialActive: status === "trialing" && canAccessApp && trialDaysLeft > 0,
    isTrialExpired: status === "trialing" && !future(user.trial_ends_at),
    trialDaysLeft, canAccessApp };
};

/** Vero quando una sessione ripresa dalla cache va chiusa, rimandando la
 *  persona al login invece di farla atterrare su /plans senza capire perche'.
 *
 *  Esclude di proposito il collaboratore rimasto senza eventi condivisi: quello
 *  ha una schermata dedicata dentro ProtectedRoute e non deve passare ne' dal
 *  login ne' da /plans. */
export const sessioneRipresaDaChiudere = (stato, pathname = "") =>
  !["/plans", "/billing/success", "/settings", "/onboarding"].includes(pathname) &&
  Boolean(stato) && !stato.canAccessApp && !stato.collaboratorNoAccess;

/** La data in cui l'accesso si e' chiuso, da mostrare nel messaggio di login.
 *  `plan_expires_at` e' il campo che regge il gate; `trial_ends_at` copre chi
 *  non ha mai comprato e si e' fermato alla fine della prova. */
export const dataFineAccesso = (user) =>
  user?.plan_expires_at || user?.trial_ends_at || null;
